import type React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '@/hooks/usePageTitle';

/* ==========================================================================
   SMS PROGRAM — Public, Showliner-branded description of the text-message
   program and the consent we collect.

   This page exists for two audiences. Anyone deciding whether to give us a
   phone number can read exactly what they are agreeing to before they agree
   to it. And carrier / messaging-provider reviewers get one public URL that
   shows who sends the texts, the verbatim consent language, and the Terms and
   Privacy Policy on the same page as the consent — which is what toll-free
   verification asks for and what an in-app screenshot can't demonstrate, since
   the opt-in lives inside a venue's live room where the venue's branding, not
   ours, is what fills the screen.

   The consent block below is a REPRODUCTION of the in-app control, labelled as
   such. It is not a working form: nothing here collects a number. Keep the
   quoted sentence character-for-character in sync with SmsConsentNotice.tsx —
   if that copy changes and this doesn't, this page becomes a false claim about
   what users were shown.
   ========================================================================== */

const SmsProgram: React.FC = () => (
  <>
    <PageTitle title="Text messages" />

    <section>
      <div className="mx-auto max-w-3xl px-4 sm:px-8 page-top">
        <h1 className="font-display text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.02] tracking-tight text-ink">
          Showliner text messages
        </h1>
        <p className="mt-5 text-lg text-grey-700 leading-relaxed">
          Showliner is show-running software for independent venues and the performers they book.
          It is operated by Swavely Creative Co. When you get a text from Showliner, we are the
          business sending it.
        </p>
      </div>
    </section>

    <section>
      <div className="mx-auto max-w-3xl px-4 sm:px-8 section pt-0 space-y-10">

        <div>
          <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">
            Giving us your number is always optional
          </h2>
          <p className="text-grey-700 leading-relaxed">
            You can use every part of Showliner without one. Nothing is gated behind a phone number,
            and we only text a number that was given to us for that purpose. There is no
            pre-checked box anywhere in the product. Consent is an explicit action you take, and
            the phone field does not even appear until you take it.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">
            What we send
          </h2>
          <ul className="space-y-3 text-grey-700 leading-relaxed">
            <li>
              <strong className="text-grey-900">Open mic alerts.</strong> If you sign up to perform
              at an open mic and opt in, we text you when you are next on the lineup, and to confirm
              we got your signup.
            </li>
            <li>
              <strong className="text-grey-900">Event and workspace invitations.</strong> If an
              organizer you are working with adds your number to a bill, we text you the invitation
              link.
            </li>
            <li>
              <strong className="text-grey-900">Account security.</strong> One-time codes to verify
              your number and to sign in when you turn on two-factor authentication.
            </li>
          </ul>
          <p className="mt-4 text-grey-700 leading-relaxed">
            We do not send marketing or promotional text messages. Message frequency varies by how
            you use Showliner. Message and data rates may apply.
          </p>
        </div>

        {/* ── The consent control, reproduced ── */}
        <div>
          <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">
            What the consent looks like
          </h2>
          <p className="text-grey-700 leading-relaxed mb-5">
            This is the control shown in Showliner at the point a phone number is collected,
            reproduced here so you can read it without signing up. It is a picture of the real
            thing, not a working form; nothing on this page collects a number.
          </p>

          <div className="rounded-lg border border-grey-300 bg-white p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-grey-400 bg-white"
              />
              <div>
                <p className="text-grey-900 font-bold">
                  I consent to receive non-marketing text messages from Showliner at the phone
                  number provided
                </p>
                <p className="mt-2 text-sm text-grey-600 leading-relaxed">
                  Showliner will text you when you&rsquo;re next on the lineup and to confirm your
                  signup. Typically 1-2 messages per event. Message and data rates may apply. Reply
                  STOP to opt out, HELP for help. See our{' '}
                  <Link to="/terms" className="underline hover:text-ink">Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="underline hover:text-ink">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-4 text-sm text-grey-600 leading-relaxed">
            The phone number field appears only after this box is checked. Leave it unchecked and we
            never ask for a number, let alone store one.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">
            Stopping the messages
          </h2>
          <p className="text-grey-700 leading-relaxed">
            Reply <strong className="text-grey-900">STOP</strong> to any Showliner text and we stop
            texting that number. Reply <strong className="text-grey-900">HELP</strong> for help, or
            email{' '}
            <a href="mailto:hello@showliner.io" className="underline hover:text-ink">
              hello@showliner.io
            </a>
            . You can also remove your number from your Showliner account at any time.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">
            Who sees your number
          </h2>
          <p className="text-grey-700 leading-relaxed">
            We do not sell, rent, or share mobile phone numbers or SMS consent with third parties
            for marketing purposes. Numbers are shared only with the messaging provider that
            delivers the message on our behalf, and only for that delivery. The full detail is in
            our{' '}
            <Link to="/privacy" className="underline hover:text-ink">Privacy Policy</Link>, and the
            terms you agree to are in our{' '}
            <Link to="/terms" className="underline hover:text-ink">Terms of Service</Link>.
          </p>
        </div>

        <div className="pt-2 border-t border-grey-200">
          <p className="text-sm text-grey-600 leading-relaxed">
            Showliner is a product of Swavely Creative Co. Questions about our text messages go to{' '}
            <a href="mailto:hello@showliner.io" className="underline hover:text-ink">
              hello@showliner.io
            </a>
            .
          </p>
        </div>

      </div>
    </section>
  </>
);

export default SmsProgram;
